// External Imports
import { useEffect, useState } from "react";

// Internal Imports
import { schema } from "../../util/schema";
import Heading from "../UI/Heading";
import SelectPair from "../UI/SelectPair";
import { toTitleCase, truncate } from "../../util/format-text";
import DetailPair from "../UI/DetailPair";
import DateFilter from "../UI/DateFilter";
import { requestAdminAPI } from "../../api/posh-api";
import LoadingSpinner from "../UI/LoadingSpinner";
import { isoDateToString } from "../../util/format-date";
import { arrayToCsv } from "../../util/arrayToCsv";

/** ========================================================================
 * * DownloadData Component
 * This component renders a form that allows user to download any sort of data.
 * ========================================================================== */

/* ======================= Camel to Title Case ======================= */
const camelToTitleCase = (str) => {
  return toTitleCase(str.replace(/([A-Z])/g, " $1"));
};

export default function DownloadData() {
  const [loading, setLoading] = useState(false);

  const firstTable = Object.keys(schema)[0];
  const [table, setTable] = useState(firstTable);
  const [columns, setColumns] = useState([]);
  const [joins, setJoins] = useState({});
  const [filter, setFilter] = useState({});
  const [sample, setSample] = useState([]);

  /* ======================= SELECT TABLE ======================= */
  const selectTable = (table) => {
    setTable(table);
  };

  /* ======================= GET COLUMN OPTIONS ======================= */
  const getOptions = (table) => {
    const options = [];
    Object.keys(schema[table]).forEach((column) => {
      // If last to characters is not Id and it does not included []
      if (
        column.slice(-2) !== "Id" &&
        !schema[table][column].includes("[") &&
        !schema[table][column].includes("]")
      )
        options.push(column);
    });
    return options;
  };

  /* ======================= GET JOIN OPTIONS ======================= */
  const getJoins = () => {
    const joins = {};
    Object.keys(schema[table]).forEach((column) => {
      if (column.slice(-2) === "Id") {
        const tableName = column.slice(0, -2);

        if (schema[tableName]) {
          joins[tableName] = [];
        }
      }
    });
    return joins;
  };

  /* ======================= SELECT COLUMN ======================= */
  const selectColumn = (option, checked) => {
    if (checked) columns.push(option);
    else columns.splice(columns.indexOf(option), 1);
    setColumns([...columns]);
  };

  /* ======================= SELECT COLUMN ======================= */
  const selectJoinColumn = (table, option, checked) => {
    if (checked) joins[table].push(option);
    else joins[table].splice(joins[table].indexOf(option), 1);
    setJoins({ ...joins });
  };

  /* ======================= SELECT ALL ======================= */
  const selectAll = (table) => {
    const options = getOptions(table);
    joins[table] = options;
    setJoins({ ...joins });
  };

  /* ======================= UNSELECT ALL ======================= */
  const unselectAll = (table) => {
    joins[table] = [];
    setJoins({ ...joins });
  };

  /* ======================= GET DATA ======================= */
  const getData = async (isSample) => {
    try {
      setLoading(true);
      // Handle Filters
      let filters = [];
      if (filter.sd && filter.ed) {
        filters.push(
          `dateCreated: {ge: "${filter.sd}"}`,
          `dateCreated: {le: "${filter.ed}"}`
        );
      }
      const c = [];
      Object.keys(joins).forEach((join) => {
        if (joins[join].length === 0) return;
        let str = `${join} {`;
        joins[join].forEach((column) => {
          str += `${column}, `;
        });
        str += "}";
        c.push(str);
      });

      const response = await requestAdminAPI("filter", {
        table: table,
        filters,
        columns: c,
        limit: isSample ? 5 : 0,
      });

      // Filter Columns
      let data = response.map((row) => {
        const newRow = {};
        columns.forEach((column) => {
          if (schema[table][column] === "Timestamp")
            newRow[column] = isoDateToString(row[column]);
          else newRow[column] = row[column];
        });
        Object.keys(joins).forEach((join) => {
          if (!row[join]) return;
          Object.keys(row[join]).forEach((column) => {
            const col = `${join}_${column}`;
            if (schema[join][column] === "Timestamp")
              newRow[col] = isoDateToString(row[join][column]);
            else newRow[col] = row[join][column] || "null";
          });
        });
        return newRow;
      });

      if (isSample) setSample(data);
      else {
        arrayToCsv(data, `${table}.csv`);
      }
      setLoading(false);
    } catch (err) {
      console.error(err);
      setLoading(false);
    }
  };

  // On Table Change
  useEffect(() => {
    setJoins(getJoins());
    setColumns(getOptions(table));
    setSample([]);
  }, [table]);

  useEffect(() => {
    if (sample.length > 0) getData(true);
    else setSample([]);
  }, [filter]);

  return (
    <div className="container gap-6">
      <Heading title="Download Data" isBackArrow={true}>
        <DateFilter
          startDate={filter.sd}
          endDate={filter.ed}
          onSubmit={(sd, ed) => setFilter({ ...filter, sd, ed })}
        />
      </Heading>
      <div className="items-start gap-4 info">
        <div className="grid gap-2 overflow-scroll no-scrollbar max-h-[500px]">
          <SelectPair
            label="Table"
            edit={true}
            options={Object.keys(schema).map((column) => ({
              id: column,
              name: toTitleCase(column),
            }))}
            inputOptions={{
              defaultValue: table,
              onChange: (e) => {
                selectTable(e.target.value);
              },
            }}
          />

          <div className="grid grid-cols-2 gap-2">
            <div
              onClick={() => setColumns(Object.keys(schema[table]))}
              className="font-semibold bg-stone-900 btn-secondary"
            >
              Select All
            </div>
            <div
              onClick={() => setColumns(["id"])}
              className="font-semibold bg-stone-900 btn-secondary"
            >
              Unselect All
            </div>
          </div>
          <div className="grid text-stone-200">
            {getOptions(table).map((option, idx) => (
              <div key={idx} className="flex items-center space-x-4">
                <input
                  type="checkbox"
                  className="checkbox"
                  checked={columns.includes(option)}
                  onChange={(e) => selectColumn(option, e.target.checked)}
                  disabled={option === "id"}
                />
                <p>{camelToTitleCase(option)}</p>
              </div>
            ))}
          </div>
        </div>
        {Object.keys(joins).map((join, idx) => (
          <div key={idx} className="grid gap-2 overflow-scroll no-scrollbar max-h-[500px]">
            <DetailPair
              label="Join Table"
              edit={true}
              inputOptions={{
                disabled: true,
                value: join,
              }}
            />

            <div className="grid grid-cols-2 gap-2 justify-items-center text-stone-400">
              <div
                onClick={() => selectAll(join)}
                className="w-full p-2 text-center cursor-pointer hover:text-stone-200"
              >
                Select All
              </div>
              <div
                onClick={() => unselectAll(join)}
                className="w-full p-2 text-center cursor-pointer hover:text-stone-200"
              >
                Unselect All
              </div>
            </div>
            <div className="grid text-stone-200">
              {getOptions(join).map((option, idx) => (
                <div key={idx} className="flex items-center space-x-4">
                  <input
                    type="checkbox"
                    className="checkbox"
                    checked={joins[join].includes(option)}
                    onChange={(e) =>
                      selectJoinColumn(join, option, e.target.checked)
                    }
                  />
                  <p>{camelToTitleCase(option)}</p>
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>
      {table && (
        <div className="flex items-start space-x-4">
          <div className="w-[65vw] px-2 pt-4 pb-8 overflow-x-scroll rounded-md bg-stone-200 text-stone-900">
            <p className="text-sm text-stone-900">SAMPLE OUTPUT:</p>
            <table className="w-full">
              <thead>
                <tr>
                  {columns.map((column, idx) => (
                    <th
                      key={idx}
                      className="px-2 text-sm border border-stone-900"
                    >
                      {column}
                    </th>
                  ))}
                  {Object.keys(joins).map((join) =>
                    joins[join].map((column, idx) => (
                      <th
                        key={idx}
                        className="px-2 text-sm border border-stone-900"
                      >
                        {join}_{column}
                      </th>
                    ))
                  )}
                </tr>
              </thead>
              <tbody>
                {sample.map((row, idx) => (
                  <tr key={idx}>
                    {columns.map((column, idx) => (
                      <td key={idx} className="text-sm border border-stone-900">
                        {truncate(row[column], 14)}
                      </td>
                    ))}
                    {Object.keys(joins).map((join) =>
                      joins[join].map((column, idx) => (
                        <td
                          key={idx}
                          className="px-2 text-sm border border-stone-900"
                        >
                          {truncate(row[`${join}_${column}`], 14)}
                        </td>
                      ))
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {loading ? (
            <LoadingSpinner />
          ) : (
            <div className="grid gap-2">
              <div
                onClick={() => getData(true)}
                className="text-sm btn-secondary"
              >
                Generate Sample
              </div>
              <div
                onClick={() => getData(false)}
                className="text-sm btn-primary"
              >
                Download CSV
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
